import { cn } from '@seen/ui'
import type { JSX } from 'react'
import { SEARCH_HIGHLIGHT_CLASS } from '#src/components/core/searchHighlight.ts'
import { buildSnippet } from '#src/hooks/useClientSideSearch.ts'

/* Word-boundary-snapped excerpt from `hiddenFieldText` with each match
 * wrapped in a <mark>. Returns `null` when no matches are found.
 *
 * `hiddenFieldMatches` is the match count within `hiddenFieldText` only;
 * when it exceeds matches in the visible window, a trailing "+N more
 * matches" counter is appended. Counts from other fields (title, AI
 * summary) are highlighted separately via `HighlightedText` and must NOT
 * be included here — otherwise the counter overcounts. */
export function SearchMatchSnippet({
  hiddenFieldText,
  stemmedTerms,
  hiddenFieldMatches,
  className,
}: {
  hiddenFieldText: string
  stemmedTerms: string[]
  hiddenFieldMatches: number
  /* Merged over the default type, for callers whose row reserves a fixed number
   * of lines: this snippet stands in for a plain preview, and at a different
   * font size it lays out to a different height, which would make rows with a
   * match taller than rows without one. */
  className?: string
}): JSX.Element | null {
  const snippet = buildSnippet(hiddenFieldText, stemmedTerms)
  if (!snippet) {
    return null
  }

  const remainder = hiddenFieldMatches - snippet.matchesInWindow

  return (
    <span
      data-testid="search-match-snippet"
      className={cn(
        'text-gray-500 text-sm italic dark:text-foreground-secondary',
        className
      )}
    >
      {snippet.truncatedLeft ? '…' : ''}"
      {snippet.parts.map((part, i) =>
        part.mark !== undefined ? (
          <mark
            // oxlint-disable-next-line react/no-array-index-key -- positional parts from a single string are stable by index
            key={i}
            className={SEARCH_HIGHLIGHT_CLASS}
          >
            {part.mark}
          </mark>
        ) : (
          // oxlint-disable-next-line react/no-array-index-key -- positional parts from a single string are stable by index
          <span key={i}>{part.text}</span>
        )
      )}
      "{snippet.truncatedRight ? '…' : ''}
      {remainder > 0 && (
        <span className="ml-1 text-gray-400 not-italic">
          {' '}
          +{remainder} more {remainder === 1 ? 'match' : 'matches'}
        </span>
      )}
    </span>
  )
}
